import type {InvoicePostingSnapshot,InvoicePostingAction,InvoiceSubmission,InvoiceDecision} from './invoice-posting-model';
import type { AccountDetails, AccountEditFields } from './account-model';
import type { FiscalSnapshot, FiscalYear, FiscalYearInput } from "./fiscal-model";
import type { AccessRole, AccessSnapshot, AccessUser, CompanyGrant, RolePermission, SecurityPolicy } from "./access-control";
import type { Account, AppState, DocumentItem, Entity, Invoice, Journal, Party, Schedule, SetupConfig } from "./types";

export interface ApiUser {
  sub: string;
  firmId: string;
  email: string;
  displayName: string;
  role: "firm_owner" | "admin" | "reviewer" | "preparer" | "viewer";
  workspaceCode?: string;
  title?: string;
  roleIds?: string[];
  roleNames?: string[];
  permissions?: RolePermission[];
  entityAccess?: CompanyGrant[];
  productAccess?: string[];
  mfaEnabled?: boolean;
  mustChangePassword?: boolean;
}

export interface ApiSession {
  accessToken: string;
  user: ApiUser;
  entityId: string | null;
}

const API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const SESSION_KEY = "ledzerly-books-api-session-v1";
const DEMO_SESSION_KEY = "ledzerly-books-demo-session-v1";

export const apiConfigured = API_BASE_URL.length > 0;

export const getSession = (): ApiSession | null => {
  try {
    const raw = localStorage.getItem(apiConfigured ? SESSION_KEY : DEMO_SESSION_KEY);
    return raw ? JSON.parse(raw) as ApiSession : null;
  } catch {
    return null;
  }
};

export const saveDemoSession = (session: ApiSession) => localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));

export const saveSession = (session: ApiSession) => localStorage.setItem(apiConfigured ? SESSION_KEY : DEMO_SESSION_KEY, JSON.stringify(session));

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(DEMO_SESSION_KEY);
};

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const session = getSession();
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && options.body !== undefined) headers.set("Content-Type", "application/json");
  if (session?.accessToken) headers.set("Authorization", `Bearer ${session.accessToken}`);
  const response = await fetch(`${API_BASE_URL}/api${path}`, { ...options, headers });
  const raw = await response.text();
  let payload: unknown = null;
  try { payload = raw ? JSON.parse(raw) : null; } catch { payload = raw; }
  if (!response.ok) {
    const message = typeof payload === "object" && payload && "error" in payload ? String(payload.error) : `API request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }
  return payload as T;
};

const json = <T>(path: string, method: string, body?: unknown) => request<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const booksApi = {
  requestSettlementReversal(entityId:string,settlementId:string,input:import('./reversal-model').ReversalInput){return request<{reversal:import('./reversal-model').SettlementReversal}>(`/entities/${entityId}/opening-settlements/${settlementId}/reversals`,{method:'POST',body:JSON.stringify(input)});},
  settlementReversalAction(entityId:string,settlementId:string,reversalId:string,action:import('./settlement-model').SettlementAction,input:{expectedRevision:number;reason:string}){return request<{reversal:import('./reversal-model').SettlementReversal}>(`/entities/${entityId}/opening-settlements/${settlementId}/reversals/${reversalId}/${action}`,{method:'POST',body:JSON.stringify(input)});},
  openingSettlements(entityId:string,kind:import('./settlement-model').PartyKind,asAt:string){return request<import('./settlement-model').SettlementSnapshot>(`/entities/${entityId}/opening-settlements?kind=${kind}&asAt=${asAt}`);},
  submitOpeningSettlement(entityId:string,input:import('./settlement-model').SettlementInput){return request<{settlement:import('./settlement-model').SettlementRecord}>(`/entities/${entityId}/opening-settlements`,{method:'POST',body:JSON.stringify(input)});},
  openingSettlementAction(entityId:string,settlementId:string,action:import('./settlement-model').SettlementAction,input:{expectedRevision:number;reason:string}){return request<{settlement:import('./settlement-model').SettlementRecord}>(`/entities/${entityId}/opening-settlements/${settlementId}/${action}`,{method:'POST',body:JSON.stringify(input)});},
  openingBalances(entityId:string){return request<import('./opening-model').OpeningSnapshot>(`/entities/${entityId}/opening-balances`);},
  submitOpening(entityId:string,input:import('./opening-model').OpeningInput){return request<{batch:import('./opening-model').OpeningBatch}>(`/entities/${entityId}/opening-balances`,{method:'POST',body:JSON.stringify(input)});},
  openingAction(entityId:string,batchId:string,action:string,input:{expectedRevision:number;reason:string}){return request<{batch:import('./opening-model').OpeningBatch}>(`/entities/${entityId}/opening-balances/${batchId}/${action}`,{method:'POST',body:JSON.stringify(input)});},
  async login(workspaceCode: string, email: string, password: string) {
    const result = await json<ApiSession>("/auth/login", "POST", { workspaceCode, email, password });
    localStorage.setItem(SESSION_KEY, JSON.stringify(result));
    return result;
  },
  async me() {
    return request<{ user: ApiUser }>("/auth/me");
  },
  async changePassword(currentPassword: string, newPassword: string) {
    return json<{ ok: boolean; user: ApiUser }>("/auth/change-password", "POST", { currentPassword, newPassword });
  },
  async state(entityId?: string) {
    return request<{ state: AppState; entityId: string }>(`/state${entityId ? `?entityId=${encodeURIComponent(entityId)}` : ""}`);
  },
  fiscalCalendar(entityId:string){return request<FiscalSnapshot>(`/entities/${entityId}/fiscal-calendar`);},
  saveFiscalYear(entityId:string,year:FiscalYearInput,existing?:FiscalYear){return existing?json(`/entities/${entityId}/fiscal-years/${existing.id}`,"PATCH",{year,expectedRevision:existing.revision}):json(`/entities/${entityId}/fiscal-years`,"POST",year);},
  fiscalAction(entityId:string,path:string,body:Record<string,unknown>){return json(`/entities/${entityId}/${path}`,"POST",body);},
  async updateSetup(entityId: string, patch: Partial<SetupConfig>) {
    return json<{ ok: boolean }>(`/entities/${entityId}/setup`, "PATCH", patch);
  },
  async generateCOA(entityId: string) {
    return json<{ ok: boolean; businessKey: string; accountCount: number }>(`/entities/${entityId}/coa/generate`, "POST");
  },
  async createAccount(entityId: string, account: Account) {
    return json<{ ok: boolean; accountId: string }>(`/entities/${entityId}/accounts`, "POST", { code: account.code, name: account.name, group: account.group, nature: account.nature, control: account.control });
  },
  accountDetails(entityId:string, accountId:string) {
    return request<AccountDetails>(`/entities/${entityId}/accounts/${accountId}`);
  },
  updateAccount(entityId:string, accountId:string, account:AccountEditFields, expectedRevision:number, reason:string) {
    return json<{ok:boolean;account:Account}>(`/entities/${entityId}/accounts/${accountId}`, "PATCH", {account,expectedRevision,reason});
  },
  async toggleAccount(entityId: string, accountId: string, active: boolean) {
    return json<{ ok: boolean }>(`/entities/${entityId}/accounts/${accountId}`, "PATCH", { active });
  },
  async createParty(entityId: string, party: Party) {
    const {id:_,name,type,gstin,pan,state,openBalance:_balance,risk:_risk,active:_active,...profile}=party;
    return json<{ ok: boolean; partyId: string }>(`/entities/${entityId}/parties`, "POST", { name,type,gstin,pan,state,profile });
  },
  async createJournal(entityId: string, journal: Journal, accounts: Account[], parties: Party[]) {
    const partyId = journal.party ? parties.find((party) => party.name === journal.party)?.id : undefined;
    return json<{ ok: boolean; journalId: string; number: string }>(`/entities/${entityId}/journals`, "POST", {
      date: journal.date, narration: journal.narration, source: journal.source, currency: journal.currency, partyId,
      sourceDocument: journal.sourceDocument, lines: journal.lines.map((line) => ({ accountId: accounts.find((account) => account.code === line.accountCode)?.id ?? line.accountCode, debit: line.debit, credit: line.credit, department: line.department, analyticGroup: line.analyticGroup, costCenter: line.costCenter, taxCode: line.taxCode }))
    });
  },
  async journalAction(journalId: string, action: "submit" | "approve" | "post" | "reverse") {
    return json<{ ok: boolean }>(`/journals/${journalId}/${action}`, "POST");
  },
  invoicePosting(entityId:string,kind:'Customer'|'Vendor') {return json<InvoicePostingSnapshot>(`/entities/${entityId}/invoice-posting?kind=${kind}`,"GET");},
  invoicePostingAction(entityId:string,invoiceId:string,action:InvoicePostingAction,input:InvoiceSubmission|InvoiceDecision) {return json<{ok:boolean;invoiceId:string}>(`/entities/${entityId}/invoice-posting/${invoiceId}/${action}`,"POST",input);},
  invoiceNumberPreview(entityId:string,type:string,date:string){return json<{number:string;provisional:boolean}>(`/entities/${entityId}/invoice-number-preview?type=${encodeURIComponent(type)}&date=${encodeURIComponent(date)}`,"GET");},
  async createInvoice(entityId: string, invoice: Invoice, accounts: Account[], parties: Party[]) {
    const partyId = invoice.partyId ?? parties.find((party) => party.name === invoice.party && party.type === (invoice.type==='Sales invoice'?'Customer':'Vendor'))?.id;
    if (!partyId) throw new Error("Select a valid party before saving the invoice.");
    return json<{ ok: boolean; invoiceId: string }>(`/entities/${entityId}/invoices`, "POST", {
      requestKey: invoice.id, number: invoice.number, date: invoice.date, type: invoice.type, partyId, placeOfSupply: invoice.placeOfSupply, currency: invoice.currency, draftDetails:invoice.draftDetails,
      source: invoice.source, rcm: invoice.rcm, tdsSection: invoice.tdsSection, tdsRate: invoice.tdsRate,
      lines: invoice.lines.map((line) => ({ ...line, accountId: accounts.find((account) => account.code === line.accountCode)?.id }))
    });
  },
  async updateInvoiceDraft(entityId:string,invoiceId:string,invoice:Invoice,accounts:Account[],parties:Party[],expectedRevision:number,reason:string){
    const partyId=invoice.partyId??parties.find(party=>party.name===invoice.party&&party.type===(invoice.type==="Sales invoice"?"Customer":"Vendor"))?.id;
    if(!partyId)throw new Error("Select a valid party before saving the draft.");
    return json<{ok:boolean;invoiceId:string;revision:number}>(`/entities/${entityId}/invoices/${invoiceId}`,"PATCH",{expectedRevision,reason,draftDetails:invoice.draftDetails,date:invoice.date,partyId,placeOfSupply:invoice.placeOfSupply,currency:invoice.currency,source:invoice.source,rcm:invoice.rcm,tdsSection:invoice.tdsSection,tdsRate:invoice.tdsRate,lines:invoice.lines.map(line=>({...line,accountId:accounts.find(account=>account.code===line.accountCode)?.id}))});
  },
  async createSchedule(entityId: string, schedule: Schedule) {
    return json<{ ok: boolean; scheduleId: string }>(`/entities/${entityId}/schedules`, "POST", { name: schedule.name, type: schedule.type, account: schedule.account, sourceInvoiceId: schedule.sourceInvoiceId, startDate: schedule.startDate, endDate: schedule.endDate, total: schedule.total, monthly: schedule.monthly, method: schedule.method });
  },
  async postSchedule(entityId: string, scheduleId: string) {
    return json<{ ok: boolean; journalId: string }>(`/entities/${entityId}/schedules/${scheduleId}/post`, "POST");
  },
  async createEntity(entity: Entity, businessNature: string) {
    return json<{ ok: boolean; entityId: string }>("/entities", "POST", { name: entity.name, country: entity.country, currency: entity.currency, gstin: entity.gstin, businessNature });
  },
  async uploadBankStatement(entityId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ ok: boolean; documentId: string; imported: number; proposedJournals: number }>(`/entities/${entityId}/bank-statements`, { method: "POST", body: form });
  },
  async bankAction(entityId: string, bankLineId: string, action: "match" | "ignore" | "create-je") {
    return json<{ ok: boolean; journalId?: string }>(`/entities/${entityId}/bank-lines/${bankLineId}/${action}`, "POST");
  },
  async uploadDocument(entityId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ ok: boolean; documentId: string }>(`/entities/${entityId}/documents/upload`, { method: "POST", body: form });
  },
  billOcrStatus(entityId:string) {return request<{configured:boolean;provider:string;maxBytes:number}>(`/entities/${entityId}/bill-ocr/status`);},
  async downloadBillSource(entityId:string,documentId:string) {
    const response=await fetch(`${API_BASE_URL}/api/entities/${entityId}/bill-ocr/${documentId}/file`,{headers:{Authorization:`Bearer ${getSession()?.accessToken??''}`}});
    if(!response.ok)throw new Error('The original bill could not be downloaded. Check access and try again.');
    const blob=await response.blob(),url=URL.createObjectURL(blob),link=document.createElement('a');
    link.href=url;link.download=`original-bill.${blob.type==='application/pdf'?'pdf':blob.type==='image/png'?'png':'jpg'}`;link.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
  },
  extractBill(entityId:string,file:File,signal?:AbortSignal) {
    const form=new FormData();form.append('file',file);
    return request<import('./bill-extraction').OcrResult>(`/entities/${entityId}/bill-ocr`,{method:'POST',body:form,signal});
  },
  async channelDocument(entityId: string, channel: "Email" | "WhatsApp" | "Client portal") {
    return json<{ ok: boolean; documentId: string }>(`/entities/${entityId}/documents/channel`, "POST", { channel });
  },
  async approveDocument(entityId: string, documentId: string) {
    return json<{ ok: boolean }>(`/entities/${entityId}/documents/${documentId}/approve`, "POST");
  },
  async advanceCloseTask(entityId: string, taskId: string) {
    return json<{ ok: boolean; status: string }>(`/entities/${entityId}/close/tasks/${taskId}/advance`, "POST");
  },
  async lockPeriod(entityId: string) {
    return json<{ ok: boolean; lockStatus: SetupConfig["lockStatus"] }>(`/entities/${entityId}/close/lock`, "POST");
  },
  async companies() { return request<{companies: import("./company-model").CompanyRecord[]}>("/admin/companies"); },
  async createCompany(company: import("./company-model").CompanyInput) { return json<{company: import("./company-model").CompanyRecord}>("/admin/companies", "POST", company); },
  async updateCompany(company: import("./company-model").CompanyRecord, reason: string) {
    const {id, updatedAt, canEdit: _canEdit, ...input} = company;
    return json<{company: import("./company-model").CompanyRecord}>(`/admin/companies/${id}`, "PATCH", {company: input, expectedUpdatedAt: updatedAt, reason});
  },
  async accessAdmin() {
    return request<AccessSnapshot & { entities: Array<{ id: string; name: string; legalName: string; country: string; currency: string; gstin: string; branches?: Array<{id:string;name:string;active?:boolean}>; active?:boolean }> }>("/admin/access");
  },
  async createAccessUser(input: {
    displayName: string;
    email: string;
    title: string;
    phone: string;
    temporaryPassword: string;
    roleIds: string[];
    entityAccess: CompanyGrant[];
    mfaEnabled: boolean;
    mustChangePassword: boolean;
  }) {
    return json<{ ok: boolean; userId: string }>("/admin/users", "POST", input);
  },
  async updateAccessUser(userId: string, input: Partial<Omit<AccessUser, "id" | "email" | "status" | "roleNames" | "lastLogin">> & { active?: boolean; temporaryPassword?: string }) {
    return json<{ ok: boolean }>(`/admin/users/${userId}`, "PATCH", input);
  },
  async createAccessRole(input: Pick<AccessRole, "name" | "description" | "category" | "permissions" | "active">) {
    return json<{ ok: boolean; roleId: string }>("/admin/roles", "POST", input);
  },
  async updateAccessRole(roleId: string, input: Pick<AccessRole, "name" | "description" | "category" | "permissions" | "active">) {
    return json<{ ok: boolean }>(`/admin/roles/${roleId}`, "PATCH", input);
  },
  async updateSecurityPolicy(input: SecurityPolicy) {
    return json<{ ok: boolean }>("/admin/security-policy", "PATCH", input);
  }
};

export { ApiError };
export { request as apiRequest, API_BASE_URL as apiBaseUrl };
